import { Popover, PopoverContent, PopoverTrigger } from '@chiroup/components/ui/popover';
import { getQuestionDisplayValue } from '@chiroup/core/functions/buildFlexibleDisplay';
import { QuestionAnswer } from '@chiroup/core/types/ComplaintSummary.type';
import React, { useEffect, useState } from 'react';
import sanitizeHtml from 'sanitize-html';
import EditingControl from './EditingControl';

interface InteractiveValueProps {
  questionId: string;
  questionData: QuestionAnswer;
  displayValue?: string | string[];
  isEditable: boolean;
  popoverOpen: boolean;
  onPopoverOpenChange: (open: boolean) => void;
  onAnswerChange: (questionId: string, newValue: string | string[], otherValue?: string) => void;
}

const InteractiveValue: React.FC<InteractiveValueProps> = ({
  questionId,
  questionData,
  displayValue,
  isEditable,
  popoverOpen,
  onPopoverOpenChange,
  onAnswerChange,
}) => {
  // Use passed displayValue or calculate it
  const finalDisplayValue = displayValue || getQuestionDisplayValue(questionId, questionData);

  // Helper function to render text with newlines as <br /> tags
  const renderTextWithNewlines = (text: string | string[]) => {
    if (Array.isArray(text)) {
      return text.map((value, index) => (
        <React.Fragment key={index}>
          {value.split('\n').map((line, lineIndex) => (
            <React.Fragment key={lineIndex}>
              {line}
              {lineIndex < value.split('\n').length - 1 && <br />}
            </React.Fragment>
          ))}
          {index < text.length - 1 && <br />}
        </React.Fragment>
      ));
    }

    return text.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < text.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  // Local state for editing - only commit on "Done"
  const [localQuestionData, setLocalQuestionData] = useState<QuestionAnswer>(questionData);

  // Reset local state when popover opens
  useEffect(() => {
    if (popoverOpen) {
      setLocalQuestionData(questionData);
    }
  }, [popoverOpen, questionData]);

  const handleLocalChange = (
    _questionId: string,
    newValue: string | string[],
    otherValue?: string,
    customInputData?: Record<string, string>
  ) => {
    setLocalQuestionData(prev => ({
      ...prev,
      selectedValues: Array.isArray(newValue) ? newValue : [newValue],
      otherValue,
      customInputData: customInputData !== undefined ? customInputData : prev.customInputData,
    }));
  };

  const handleDone = () => {
    // Only commit changes when Done is clicked
    // Cast to any to handle the customInputData parameter
    (onAnswerChange as any)(
      questionId,
      localQuestionData.selectedValues,
      localQuestionData.otherValue,
      localQuestionData.customInputData
    );
    onPopoverOpenChange(false);
  };

  if (!isEditable) {
    return (
      <span className="font-bold" style={{ color: '#93cc68' }}>
        {renderTextWithNewlines(finalDisplayValue)}
      </span>
    );
  }

  return (
    <Popover open={popoverOpen} onOpenChange={onPopoverOpenChange} modal={false}>
      <PopoverTrigger asChild>
        <span
          className="font-bold cursor-pointer hover:bg-primary-50 rounded transition-colors duration-200"
          style={{ color: '#93cc68' }}
          title="Click to edit"
        >
          {renderTextWithNewlines(finalDisplayValue)}
        </span>
      </PopoverTrigger>
      <PopoverContent
        className="w-[calc(100vw-2rem)] max-w-[500px]"
        align="start"
        sideOffset={8}
        onOpenAutoFocus={e => e.preventDefault()}
        onCloseAutoFocus={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        onPointerDownOutside={e => {
          // Prevent closing on pointer down outside
          e.preventDefault();
        }}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <h4
              className="text-sm font-semibold"
              dangerouslySetInnerHTML={{
                __html: sanitizeHtml(questionData?.questionText || 'Edit Answer', {
                  allowedTags: ['u', 'b', 'i', 'em', 'strong', 'sup', 'sub', 'br'],
                  allowedAttributes: {},
                }),
              }}
            />
          </div>
          <div className="-mx-2 px-2 -my-1 py-1">
            <EditingControl
              questionId={questionId}
              questionData={localQuestionData}
              onAnswerChange={handleLocalChange}
            />
          </div>
          <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-3 flex justify-between items-center mt-4">
            <span className="text-sm font-medium text-primary-800 dark:text-primary-300"></span>
            <div className="flex gap-2">
              <button
                onClick={() => onPopoverOpenChange(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600 border border-gray-300 dark:border-gray-600 rounded-md transition-colors duration-150"
              >
                Cancel
              </button>
              <button
                onClick={handleDone}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 dark:bg-primary-700 dark:hover:bg-primary-800 rounded-md transition-colors duration-150"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default InteractiveValue;
